import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import ar from "./locales/ar.json";
import fr from "./locales/fr.json";
import es from "./locales/es.json";

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
  { code: "fr", label: "Français", dir: "ltr" },
  { code: "es", label: "Español", dir: "ltr" },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]["code"];

/** Geo-based language mapping by region */
const GEO_LANGUAGE_MAP: Record<string, SupportedLanguage> = {
  SA: "ar", AE: "ar", KW: "ar", QA: "ar", OM: "ar", BH: "ar",
  EG: "ar", JO: "ar", LB: "ar", IQ: "ar", LY: "ar", SD: "ar",
  TN: "ar", DZ: "ar", MA: "ar", YE: "ar", PS: "ar", SY: "ar",
  FR: "fr", BE: "fr", CH: "fr", CA: "fr",
  ES: "es", MX: "es", AR: "es", CO: "es", CL: "es", PE: "es",
  VE: "es", EC: "es", GT: "es", CU: "es", BO: "es", DO: "es",
  HN: "es", PY: "es", SV: "es", NI: "es", CR: "es", PA: "es",
  UY: "es", PR: "es",
};

export function getLanguageFromCountry(countryCode: string): SupportedLanguage {
  return GEO_LANGUAGE_MAP[countryCode?.toUpperCase()] || "en";
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
      fr: { translation: fr },
      es: { translation: es },
    },
    fallbackLng: "en",
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: "vazoul_language",
      caches: ["localStorage"],
    },
  });

/** Apply direction and lang to <html> */
export function applyLanguageDirection(lng: string) {
  const langConfig = SUPPORTED_LANGUAGES.find((l) => l.code === lng);
  const dir = langConfig?.dir || "ltr";
  document.documentElement.setAttribute("dir", dir);
  document.documentElement.setAttribute("lang", lng);
}

// Sync language to backend profile
async function syncLanguageToProfile(lng: string) {
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.id) {
      await supabase.from("profiles").update({ preferred_language: lng }).eq("user_id", session.user.id);
    }
  } catch {
    // Silent fail — profile sync is best-effort
  }
}

// Listen for language changes
i18n.on("languageChanged", (lng) => {
  applyLanguageDirection(lng);
  localStorage.setItem("vazoul_language", lng);
  syncLanguageToProfile(lng);
});

// Apply initial direction
applyLanguageDirection(i18n.language);

export default i18n;
